import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import MostVisitedPagerPage from "./MostVisitedPagerPage";
import "./MostVisitedPager.css";

const FILTERS = ["24h", "week", "month"];
const VISUAL_FILTERS = [...FILTERS].reverse();

const dispatchMostVisitedPagerProgress = (progress) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("mostVisitedPagerProgress", {
      detail: { progress },
    })
  );
};

const getNewsCardIds = (item) => {
  const ids = [];

  if (item?.link) {
    ids.push(`news-${encodeURIComponent(item.link)}`);
  }

  const newsId = item?.newsId ?? item?.id;

  if (newsId !== undefined && newsId !== null) {
    ids.push(`news-${encodeURIComponent(String(newsId))}`);
  }

  return ids;
};

const MostVisitedPagerIOS = ({
  pages = {},
  favicons = {},
  onNewsOpen,
  activeFilter,
  onFilterChange,
  scrollToTopSignal,
  onActivePageAtTopChange,
  savedLinks,
  onToggleSaved,
  onShare,
  onView,
  target,
  onTargetHandled,
}) => {
  const containerRef = useRef(null);
  const pageRefs = useRef({});
  const scrollTimeoutRef = useRef(null);
  const progressRafRef = useRef(null);
  const pendingProgressRef = useRef(0);
  const internalPageChangeRef = useRef(false);

  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(0, FILTERS.indexOf(activeFilter))
  );

  const activeIndexRef = useRef(activeIndex);

  useLayoutEffect(() => {
    const index = Math.max(0, FILTERS.indexOf(activeFilter));
    const container = containerRef.current;

    if (internalPageChangeRef.current && index === activeIndexRef.current) {
      internalPageChangeRef.current = false;
      return;
    }

    activeIndexRef.current = index;
    setActiveIndex(index);

    const page = pageRefs.current[activeFilter];
    const pageScrollTop = page?.scrollTop || 0;

    onActivePageAtTopChange?.(pageScrollTop === 0);

    if (!container) return;

    const visualIndex = VISUAL_FILTERS.indexOf(activeFilter);
    if (visualIndex < 0) return;

    dispatchMostVisitedPagerProgress(visualIndex);

    const targetLeft = visualIndex * container.clientWidth;

    container.scrollTo({
      left: targetLeft,
      top: 0,
      behavior: "auto",
    });

    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => {
        container.scrollTo({
          left: targetLeft,
          top: 0,
          behavior: "auto",
        });
      });
    }
  }, [activeFilter, onActivePageAtTopChange]);

  const activateVisualIndex = useCallback(
    (visualIndex) => {
      const safeVisualIndex = Math.max(
        0,
        Math.min(visualIndex, VISUAL_FILTERS.length - 1)
      );

      const nextFilter = VISUAL_FILTERS[safeVisualIndex];
      const newIndex = FILTERS.indexOf(nextFilter);

      if (!nextFilter || newIndex < 0 || newIndex === activeIndexRef.current) {
        return;
      }

      activeIndexRef.current = newIndex;
      setActiveIndex(newIndex);

      const page = pageRefs.current[nextFilter];
      const pageScrollTop = page?.scrollTop || 0;

      onActivePageAtTopChange?.(pageScrollTop === 0);

      internalPageChangeRef.current = true;
      onFilterChange?.(nextFilter);
    },
    [onActivePageAtTopChange, onFilterChange]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const publishProgress = (progress) => {
      pendingProgressRef.current = progress;

      if (progressRafRef.current !== null) return;

      if (typeof requestAnimationFrame !== "function") {
        dispatchMostVisitedPagerProgress(pendingProgressRef.current);
        return;
      }

      progressRafRef.current = requestAnimationFrame(() => {
        progressRafRef.current = null;
        dispatchMostVisitedPagerProgress(pendingProgressRef.current);
      });
    };

    const handleScrollEnd = () => {
      const pageWidth = container.clientWidth;
      if (!pageWidth) return;

      const visualIndex = Math.round(container.scrollLeft / pageWidth);
      const safeProgress = Math.max(
        0,
        Math.min(visualIndex, VISUAL_FILTERS.length - 1)
      );

      publishProgress(safeProgress);
      activateVisualIndex(visualIndex);
    };

    const handleScroll = () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const pageWidth = container.clientWidth;

      if (pageWidth) {
        const progress = Math.max(
          0,
          Math.min(
            container.scrollLeft / pageWidth,
            VISUAL_FILTERS.length - 1
          )
        );

        publishProgress(progress);
      }

      scrollTimeoutRef.current = setTimeout(handleScrollEnd, 120);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleScroll);

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }

      if (
        progressRafRef.current !== null &&
        typeof cancelAnimationFrame === "function"
      ) {
        cancelAnimationFrame(progressRafRef.current);
      }

      // The effect can be recreated after a parent render while a frame is
      // still queued. Always release the RAF lock after cleanup; otherwise
      // publishProgress() sees a stale non-null id and permanently stops
      // dispatching the pill movement event after a few tab changes.
      progressRafRef.current = null;
    };
  }, [activateVisualIndex]);

  useEffect(() => {
    if (!scrollToTopSignal) return;

    const activePage = pageRefs.current[FILTERS[activeIndex]];

    activePage?.scrollTo({
      top: 0,
      behavior: "auto",
    });

    onActivePageAtTopChange?.(true);
  }, [activeIndex, onActivePageAtTopChange, scrollToTopSignal]);

  useEffect(() => {
    if (!target?.link) return;

    const targetFilter = FILTERS.find((filter) =>
      (pages[filter] || []).some((item) => item.link === target.link)
    );

    if (!targetFilter) return;

    if (targetFilter !== activeFilter) {
      onFilterChange?.(targetFilter);
      return;
    }

    const runTargetScroll = () => {
      const page = pageRefs.current[targetFilter];
      if (!page) return;

      const targetIds = getNewsCardIds(target);
      const element = Array.from(page.querySelectorAll(".news-card")).find(
        (card) => targetIds.includes(card.id)
      );

      if (!element) return;

      const pageRect = page.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const targetTop = Math.max(
        0,
        page.scrollTop +
          elementRect.top -
          pageRect.top -
          page.clientHeight / 2 +
          elementRect.height / 2
      );

      page.scrollTo({
        top: targetTop,
        behavior: "smooth",
      });

      element.classList.add("news-card--highlight");

      const clearHighlight = () => {
        element.classList.remove("news-card--highlight");
      };

      if (typeof window !== "undefined") {
        window.setTimeout(clearHighlight, 2000);
      } else {
        clearHighlight();
      }

      onActivePageAtTopChange?.(targetTop === 0);
      onTargetHandled?.();
    };

    const timeoutId =
      typeof window !== "undefined"
        ? window.setTimeout(runTargetScroll, 120)
        : setTimeout(runTargetScroll, 120);

    return () => {
      if (typeof window !== "undefined") {
        window.clearTimeout(timeoutId);
      } else {
        clearTimeout(timeoutId);
      }
    };
  }, [
    activeFilter,
    onActivePageAtTopChange,
    onFilterChange,
    onTargetHandled,
    pages,
    target,
  ]);

  const handlePageScroll = useCallback(
    (filter) => {
      if (filter !== FILTERS[activeIndex]) return;

      const page = pageRefs.current[filter];
      const pageScrollTop = page?.scrollTop || 0;

      onActivePageAtTopChange?.(!page || pageScrollTop === 0);
    },
    [activeIndex, onActivePageAtTopChange]
  );

  return (
    <div className="most-visited-pager most-visited-pager--ios">
      <div className="most-visited-pager__container" ref={containerRef}>
        {VISUAL_FILTERS.map((filter, visualIndex) => (
          <MostVisitedPagerPage
            key={`${filter}-${visualIndex}`}
            ref={(node) => {
              if (node) {
                pageRefs.current[filter] = node;
              } else {
                delete pageRefs.current[filter];
              }
            }}
            filter={filter}
            pageItems={pages[filter] || []}
            onScroll={() => handlePageScroll(filter)}
            favicons={favicons}
            onNewsOpen={onNewsOpen}
            savedLinks={savedLinks}
            onToggleSaved={onToggleSaved}
            onShare={onShare}
            onView={onView}
          />
        ))}
      </div>
    </div>
  );
};

export default MostVisitedPagerIOS;
